<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import moment from 'moment';
import { saveAs } from 'file-saver';
import MultiSelect from 'primevue/multiselect';
import Paginator from 'primevue/paginator';
import tilePattern from '@/assets/images/pattern-project.svg';
import { labOrdersApi, labsApi, type Lab, type LabOrder } from '@/services/api/emsl';
import { usePagination } from '@/composables/usePagination';
import { useGridList } from '@/composables/useGridList';
import { useNotifier } from '@/composables/notifier';
import { useAppDialog } from '@/composables/useAppDialog';
import { useDateRangePresets } from '@/composables/useDateRangePresets';
import DateRangeDropdown from '@/components/DateRangeDropdown.vue';
import JsonViewerDialog from '@/components/JsonViewerDialog.vue';

// Mirrors LAB_ORDERS_MAX_LOOKBACK_DAYS on the server.
const MAX_LOOKBACK_DAYS = 730;

const {
  preset: datePreset,
  dateRange,
  options: presetOptions,
  minPickerDate,
  maxPickerDate,
} = useDateRangePresets({ defaultPreset: '90d', maxLookbackDays: MAX_LOOKBACK_DAYS });

const paging = usePagination(25);
const grid = useGridList();
const labOrders = ref<LabOrder[]>([]);
const search = ref('');
const loading = ref(false);
const notify = useNotifier();
const dialog = useAppDialog();

const labs = ref<Lab[]>([]);
const labsFilter = ref<Lab[]>([]);

/** Static set, mirrors legacy `labOrderStatus` in labOrders.js. */
interface StatusOption {
  name: 'RESERVED' | 'IN PROGRESS' | 'COMPLETED' | 'CANCELLED';
}
const STATUS_OPTIONS: StatusOption[] = [
  { name: 'RESERVED' },
  { name: 'IN PROGRESS' },
  { name: 'COMPLETED' },
  { name: 'CANCELLED' },
];
const statusFilter = ref<StatusOption[]>([]);


async function reload(): Promise<void> {
  loading.value = true;
  try {
    const [since, until] = dateRange.value;
    const result = await labOrdersApi.index(paging.page.value, paging.perPage.value, {
      query: search.value,
      labIds: labsFilter.value.map((l) => l.id),
      statuses: statusFilter.value.map((s) => s.name),
      ...(since ? { since: since.toISOString() } : {}),
      ...(until ? { until: until.toISOString() } : {}),
    });
    labOrders.value = result.data;
    paging.setCount(result.pagingInfo.count);
    if (result.clampedToDays !== undefined) {
      notify.warn(
        `Date range narrowed to the last ${result.clampedToDays} days — the server caps how far back you can query.`,
        'Date range clamped',
      );
    }
  } finally {
    loading.value = false;
  }
}

/** New search or filter change → page 1, otherwise paginating past the result
 *  set on the current page returns an empty slice even when matches exist. */
async function runSearch(): Promise<void> {
  paging.setPage(1);
  await reload();
}

async function clearSearch(): Promise<void> {
  if (search.value.length === 0) return;
  search.value = '';
  await runSearch();
}

/** PrimeVue Paginator emits a PageState on change. It's 0-indexed; our
 *  pagination + the Rails API are 1-indexed. */
async function onPaginate(event: { page: number; rows: number }): Promise<void> {
  paging.perPage.value = event.rows;
  paging.setPage(event.page + 1);
  await reload();
}

// Filter dropdowns immediately re-query — matches legacy `ng-change`.
watch(labsFilter, () => void runSearch());
watch(statusFilter, () => void runSearch());
// Wait for *both* sides of the range to settle before requerying, so the
// user picking a new range doesn't fire a half-bounded request mid-pick.
watch(
  dateRange,
  ([s, u]) => {
    if (s && u) void runSearch();
  },
  { deep: true },
);

async function loadLabs(): Promise<void> {
  const result = await labsApi.index();
  labs.value = Array.isArray(result) ? result : result.data;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const m = moment(iso);
  return m.isValid() ? m.format('MM/DD/YYYY hh:mm:ss A') : '—';
}

function statusLabel(order: LabOrder): string {
  const name = order.status?.name ?? '—';
  return order.appSurveyGuid ? name : `MANUAL ORDER, ${name}`;
}

function showCOC(order: LabOrder): boolean {
  if (!order.appSurveyGuid) return false;
  if (order.templateName === 'Lab Reservation') return false;
  if (order.templateName === 'Paper COC') return false;
  return true;
}

function showJSON(order: LabOrder): boolean {
  if (!order.appSurveyGuid) return false;
  if (order.templateName === 'Lab Reservation') return false;
  return true;
}

async function viewCOC(order: LabOrder): Promise<void> {
  if (!order.appSurveyGuid) return;
  try {
    const bytes = await labOrdersApi.orderCOC(order.appSurveyGuid);
    const blob = new Blob([bytes], { type: 'application/pdf' });
    saveAs(blob, `${order.reservationCode}.pdf`);
  } catch {
    notify.error('Could not download COC');
  }
}

async function viewJSON(order: LabOrder): Promise<void> {
  if (!order.appSurveyGuid) return;
  try {
    const data = await labOrdersApi.orderJson(order.appSurveyGuid);
    dialog.open(JsonViewerDialog, {
      header: `Order JSON — ${order.reservationCode}`,
      width: '64rem',
      data: { json: data, label: order.reservationCode },
    });
  } catch {
    notify.error('Could not load JSON');
  }
}

onMounted(() => {
  void loadLabs();
  void reload();
});
</script>

<template>
  <section class="lab-orders">
    <header class="lab-orders__header">
      <h1>Lab Orders</h1>
      <div class="lab-orders__view-toggle" role="group" aria-label="View type">
        <button
          type="button"
          :aria-pressed="grid.viewType.value === 'list'"
          :class="{ 'is-active': grid.viewType.value === 'list' }"
          title="List view"
          @click="grid.switchViewType('list')"
        >
          ☰ List
        </button>
        <button
          type="button"
          :aria-pressed="grid.viewType.value === 'grid'"
          :class="{ 'is-active': grid.viewType.value === 'grid' }"
          title="Grid view"
          @click="grid.switchViewType('grid')"
        >
          ▦ Grid
        </button>
      </div>
    </header>

    <form class="lab-orders__controls" @submit.prevent="runSearch">
      <div class="lab-orders__search-field">
        <input
          v-model="search"
          type="text"
          placeholder="Search by code, name, email, or inspector…"
        />
        <button
          v-if="search.length > 0"
          type="button"
          class="lab-orders__clear"
          aria-label="Clear search"
          title="Clear search"
          @click="clearSearch"
        >
          ×
        </button>
      </div>

      <MultiSelect
        v-model="labsFilter"
        :options="labs"
        option-label="name"
        placeholder="Filter by Lab"
        display="chip"
        filter
        class="lab-orders__filter"
        :max-selected-labels="2"
      />

      <MultiSelect
        v-model="statusFilter"
        :options="STATUS_OPTIONS"
        option-label="name"
        placeholder="Filter by Status"
        display="chip"
        class="lab-orders__filter lab-orders__filter--status"
      />

      <DateRangeDropdown
        v-model:preset="datePreset"
        v-model:date-range="dateRange"
        :options="presetOptions"
        :min-date="minPickerDate"
        :max-date="maxPickerDate"
        class="lab-orders__date"
      />

      <button type="submit">Search</button>
    </form>

    <p v-if="loading" class="lab-orders__state">Loading…</p>
    <p v-else-if="labOrders.length === 0" class="lab-orders__state">No lab orders.</p>

    <!-- ── LIST VIEW ───────────────────────────────────────────────────── -->
    <table v-else-if="grid.viewType.value === 'list'" class="lab-orders__table">
      <thead>
        <tr>
          <th>Reservation Code</th>
          <th>Reserved At</th>
          <th>Lab</th>
          <th>Order Summary</th>
          <th>Status</th>
          <th class="lab-orders__contact-th">
            <div>Email</div>
            <div>Name</div>
            <div>EMSL ID</div>
          </th>
          <th class="lab-orders__actions-th" />
        </tr>
      </thead>
      <tbody>
        <tr v-for="o in labOrders" :key="o.appSurveyGuid ?? o.reservationCode">
          <td>{{ o.reservationCode }}</td>
          <td>{{ formatDate(o.reservedAt) }}</td>
          <td>{{ o.labName ?? '—' }}</td>
          <td>
            <div v-for="(line, idx) in o.orders ?? []" :key="idx" class="lab-orders__line">
              <div>{{ line.quantity }}: {{ line.analysis_type ?? line.test_method }}</div>
              <div v-if="line.turnaround" class="lab-orders__turnaround">
                {{ line.turnaround }}
              </div>
            </div>
            <span v-if="(o.orders ?? []).length === 0">—</span>
          </td>
          <td>{{ statusLabel(o) }}</td>
          <td class="lab-orders__contact">
            <div>{{ o.customerEmail ?? o.erInspectorEmail ?? '—' }}</div>
            <div>{{ o.customerName ?? o.erInspectorName ?? '—' }}</div>
            <div>{{ o.emslBillingId ?? o.erLabBillingId ?? '—' }}</div>
          </td>
          <td class="lab-orders__actions">
            <button v-if="showCOC(o)" type="button" @click="viewCOC(o)">COC</button>
            <button v-if="showJSON(o)" type="button" @click="viewJSON(o)">JSON</button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- ── GRID VIEW ──────────────────────────────────────────────────── -->
    <ul v-else class="lab-orders__tiles">
      <li
        v-for="o in labOrders"
        :key="o.appSurveyGuid ?? o.reservationCode"
        class="lab-orders__tile"
        :style="{
          backgroundImage:
            `linear-gradient(rgba(17, 24, 39, 0.78), rgba(17, 24, 39, 0.78)), url(${tilePattern})`,
        }"
      >
        <header class="lab-orders__tile-header">
          <h3>{{ o.reservationCode }}</h3>
          <p class="lab-orders__tile-lab">{{ o.labName ?? '—' }}</p>
          <p class="lab-orders__tile-date">{{ formatDate(o.reservedAt) }}</p>
        </header>
        <p class="lab-orders__tile-status">{{ statusLabel(o) }}</p>
        <ul v-if="(o.orders ?? []).length > 0" class="lab-orders__tile-lines">
          <li v-for="(line, idx) in o.orders ?? []" :key="idx">
            <div>{{ line.quantity }}: {{ line.analysis_type ?? line.test_method }}</div>
            <div v-if="line.turnaround" class="lab-orders__turnaround">
              {{ line.turnaround }}
            </div>
          </li>
        </ul>
        <footer class="lab-orders__tile-actions">
          <button v-if="showCOC(o)" type="button" @click="viewCOC(o)">COC</button>
          <button v-if="showJSON(o)" type="button" @click="viewJSON(o)">JSON</button>
        </footer>
      </li>
    </ul>

    <Paginator
      v-if="paging.count.value > 0"
      class="lab-orders__pager"
      :rows="paging.perPage.value"
      :total-records="paging.count.value"
      :first="(paging.page.value - 1) * paging.perPage.value"
      :rows-per-page-options="[10, 25, 50, 100]"
      template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      current-page-report-template="Showing {first} – {last} of {totalRecords}"
      @page="onPaginate"
    />
  </section>
</template>

<style scoped>
.lab-orders {
}
.lab-orders__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}
.lab-orders__view-toggle {
  display: inline-flex;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  overflow: hidden;
}
.lab-orders__view-toggle button {
  padding: 0.375rem 0.875rem;
  background: #fff;
  color: #4b5563;
  border: 0;
  border-right: 1px solid #d1d5db;
  font-size: 0.875rem;
  cursor: pointer;
}
.lab-orders__view-toggle button:last-child {
  border-right: 0;
}
.lab-orders__view-toggle button.is-active {
  background: #2563eb;
  color: #fff;
}

.lab-orders__controls {
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
  flex-wrap: wrap;
  /* Keep the search/filter bar in view while the results scroll beneath it.
   * --app-header-offset (set on the app root) is the sticky masthead's height
   * in top layout, and 0 in the side-ribbon layout. The negative inline margins
   * bleed the bar's background out to the .app__main padding edges (1.5rem) so
   * scrolling grid-tile shadows don't peek past its sides. */
  position: sticky;
  top: var(--app-header-offset, 0px);
  z-index: 20;
  background: #fafbfc;
  margin: 0 -1.5rem 1rem;
  padding: 0.75rem 1.5rem;
}
.lab-orders__search-field {
  position: relative;
  flex: 1 1 18rem;
  display: flex;
  min-width: 14rem;
}
.lab-orders__search-field input {
  flex: 1;
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}
.lab-orders__filter {
  min-width: 14rem;
  max-width: 22rem;
}
.lab-orders__filter--status {
  min-width: 12rem;
}
.lab-orders__date {
  flex-shrink: 0;
}
.lab-orders__clear {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.25rem;
  height: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: #e5e7eb;
  color: #4b5563;
  border: 0;
  border-radius: 50%;
  font-size: 0.875rem;
  line-height: 1;
  cursor: pointer;
}
.lab-orders__clear:hover {
  background: #d1d5db;
  color: #1f2937;
}
.lab-orders__controls > button[type='submit'] {
  padding: 0.5rem 1rem;
  background: #2563eb;
  color: #fff;
  border: 0;
  border-radius: 0.375rem;
  cursor: pointer;
}

.lab-orders__state {
  color: #6b7280;
}

/* List view */
.lab-orders__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
}
.lab-orders__table th,
.lab-orders__table td {
  padding: 0.625rem 0.75rem;
  border-bottom: 1px solid #f3f4f6;
  text-align: left;
  vertical-align: top;
}
.lab-orders__table thead th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}
.lab-orders__table tbody tr:hover {
  background: #fafbff;
}
/* Header divs inherit the standard thead styling (0.875rem / 600 / #374151).
   Only the body cells get the smaller, lighter contact-info treatment. */
.lab-orders__contact div {
  font-size: 0.75rem;
  line-height: 1.4;
}
.lab-orders__line {
  margin-bottom: 0.25rem;
}
.lab-orders__line:last-child {
  margin-bottom: 0;
}
.lab-orders__turnaround {
  color: #6b7280;
  font-size: 0.75rem;
}
.lab-orders__actions-th {
  width: 8rem;
}
.lab-orders__actions {
  white-space: nowrap;
}
.lab-orders__actions button {
  margin-right: 0.25rem;
  padding: 0.25rem 0.625rem;
  background: #2563eb;
  color: #fff;
  border: 0;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}
.lab-orders__actions button:hover {
  background: #1d4ed8;
}

/* Grid view */
.lab-orders__tiles {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(18rem, 1fr));
  gap: 1rem;
  list-style: none;
  padding: 0;
  margin: 0;
}
.lab-orders__tile {
  display: flex;
  flex-direction: column;
  /* Same SVG pattern the legacy AngularJS `.tiles__item--projects` used.
   * The URL is injected via `:style` on the element (Vite emits a hashed
   * asset URL we can't hard-code in a scoped stylesheet). */
  background-color: transparent;
  background-position: 0 0;
  background-repeat: repeat;
  border-radius: 0.5rem;
  box-shadow: 2px 3px 10px 2px rgba(31, 41, 51, 0.34);
  padding: 0;
  overflow: hidden;
  min-height: 14rem;
}
/* Dark overlay lives on the tile itself (via the inline linear-gradient over
 * the pattern), so children stay transparent — no per-section panel that
 * would leave the auto-margin gap above the actions footer uncovered. */
.lab-orders__tile > * {
  color: #f9fafb;
  padding: 0.75rem 1rem;
  margin: 0;
}
.lab-orders__tile > * + * {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.lab-orders__tile-header h3 {
  color: #fff;
}
.lab-orders__tile-lab,
.lab-orders__tile-date {
  color: #d1d5db;
}
.lab-orders__tile-status {
  color: #93c5fd;
}
.lab-orders__tile-lines {
  color: #e5e7eb;
}
.lab-orders__tile-lines li {
  border-top-color: rgba(255, 255, 255, 0.12);
}
.lab-orders__turnaround {
  color: #d1d5db;
}
.lab-orders__tile-header h3 {
  font-size: 1rem;
  font-weight: 600;
}
.lab-orders__tile-lab,
.lab-orders__tile-date {
  font-size: 0.75rem;
  line-height: 1.4;
}
.lab-orders__tile-status {
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.lab-orders__tile-lines {
  list-style: none;
  font-size: 0.75rem;
}
.lab-orders__tile-lines li {
  padding: 0.25rem 0;
}
.lab-orders__tile-actions {
  margin-top: auto;
}
.lab-orders__tile-actions button {
  margin-right: 0.25rem;
  padding: 0.25rem 0.625rem;
  background: #2563eb;
  color: #fff;
  border: 0;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}

.lab-orders__pager {
  margin-top: 1.5rem;
  justify-content: center;
}
</style>
